/*
 * Internal helper: Do NOT import outside of logger.ts.
 * Purpose: Forward sanitized warn/error events to Rollbar if available in the browser global.
 */

import { DEBUG_LEVEL } from '../../../config/configConstants';

export type RollbarLike = {
    error?: (message: string, error?: unknown, payload?: unknown) => void;
    warning?: (message: string, error?: unknown, payload?: unknown) => void;
};

type RollbarMethodName = keyof Pick<RollbarLike, 'error' | 'warning'>;

/**
 * Invoke a Rollbar logging method (error or warning) with the right argument shape.
 * Mirrors Rollbar signature precedence:
 *  - message, error, custom
 *  - message, error
 *  - message, custom
 *  - message
 */
const invokeRollbar = (
    rollbar: RollbarLike,
    methodName: RollbarMethodName,
    message: string,
    error: unknown,
    payload: { custom: { extra: unknown[] } } | undefined,
) => {
    const method = rollbar[methodName];
    if (!method) return;

    // Preserve Rollbar's argument precedence exactly.
    if (error) {
        if (payload) {
            method.call(rollbar, message, error, payload);
        } else {
            method.call(rollbar, message, error);
        }
        return;
    }

    if (payload) {
        method.call(rollbar, message, payload);
        return;
    }

    method.call(rollbar, message);
};

/**
 * For internal logger usage only. Relies on prior sanitization performed in logger.ts.
 */
export const forwardWarnOrErrorToRollbar = (logLevel: number, values: unknown[]) => {
    /**
     * Expectations (done by logger.ts before calling us):
     *  - values[0] is ideally a string message (not guaranteed)
     *  - remaining values may include: an Error (at most one is forwarded) + arbitrary metadata
     *  - all values are already considered safe / sanitized for client logging
     */
    try {
        // Early exit: only WARN / ERROR levels are forwarded. Avoid touching globals otherwise.
        const isErrorLevel = logLevel === DEBUG_LEVEL.ERROR;
        const isWarnLevel = logLevel === DEBUG_LEVEL.WARN;
        if (!isErrorLevel && !isWarnLevel) return;

        // Resolve a Rollbar-like instance from either window or globalThis. (Capacitor / hybrid contexts vary.)
        const rollbar: RollbarLike | undefined = (() => {
            if (typeof window !== 'undefined') {
                const maybe = (window as unknown as { Rollbar?: RollbarLike }).Rollbar;
                if (maybe) return maybe;
            }
            return (globalThis as unknown as { Rollbar?: RollbarLike }).Rollbar;
        })();
        if (!rollbar) return; // No Rollbar present; silently ignore.

        // Normalise the primary message. If the first value is not a string we still emit something traceable.
        const message = typeof values[0] === 'string' ? (values[0] as string) : '[no-message]';

        // Extract the first Error (if any) plus all remaining non-undefined extras for context.
        let firstError: unknown = null;
        const extra: unknown[] = [];
        for (let i = 1; i < values.length; i += 1) {
            const value = values[i];
            if (!firstError && value instanceof Error) {
                firstError = value;
                continue;
            }
            if (value !== undefined) extra.push(value);
        }

        // Rollbar supports sending arbitrary data via payload.custom; we group everything after message/error there.
        const payload = extra.length ? { custom: { extra } } : undefined;

        // Dispatch to the appropriate Rollbar method. (We intentionally do not fallback: warn != error.)
        if (isErrorLevel && rollbar.error) {
            invokeRollbar(rollbar, 'error', message, firstError, payload);
            return;
        }
        if (isWarnLevel && rollbar.warning) {
            invokeRollbar(rollbar, 'warning', message, firstError, payload);
            return;
        }
        // If the specific Rollbar method is missing, we silently drop.
    } catch (error: unknown) {
        // Last-resort: never let forwarding break app flow. Log a minimal console error for internal debugging.
        const errorMessage =
            error && typeof error === 'object' && 'message' in error
                ? (error as { message?: string }).message
                : undefined;
        console.error('[rollbar-forward-failed]', errorMessage);
    }
};
